import React, { useContext } from "react";
import { styled } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import { Container, Typography, Grid } from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import { AccountContext } from "../../store/AccountContext";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import { Navigate } from "react-router-dom";

/**
 * PM5 T2 (money path, batch "freetrial-buynow") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * **SUBSCRIPTIONS SURFACE.** The "trials are switched off" state of the signup
 * funnel — rendered by `FreeTrialLanding`, `Onboarding/Public` and
 * `Onboarding/BuyPublic` when `overview.doors.trialSMSEnabled` is false. The
 * conversion is styling-only: the `loggedIn` redirect and the `!loggedIn`
 * Header gate are byte-identical.
 */

// was: useStyles().background — a bare <div>, which has no `sx` prop.
const Background = styled("div")(({ theme }) => ({
  height: "50vh",
  backgroundColor: theme.palette.background.ffNavyLighter,
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  width: "auto",
  display: "flex",
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
}));

// was: useStyles().container
const containerSx = {
  textAlign: "center",
};

// was: useStyles().title
const titleSx = (theme: Theme) => ({
  letterSpacing: "-2px",
  fontSize: "1.6rem",
  lineHeight: "1.8rem",
  fontWeight: 600,
  textAlign: "center",
  marginBottom: "18px",
  textShadow: "-2px 2px 2px #171717",
  color: "#fff",
  "& span": {
    color: "background.ffGreen",
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "2.4rem",
    lineHeight: "2.6rem",
  },
});

// was: useStyles().titleLarge
const titleLargeSx = (theme: Theme) => ({
  letterSpacing: "-2px",
  textAlign: "center",
  fontSize: "2.6rem",
  lineHeight: "2.8rem",
  fontWeight: 600,
  marginBottom: "8px",
  textShadow: "-2px 2px 2px #171717",
  [theme.breakpoints.up("sm")]: {
    fontSize: "4.4rem",
    lineHeight: "4rem",
  },
});

// was: useStyles().bodyText
const bodyTextSx = (theme: Theme) => ({
  fontWeight: 500,
  fontSize: "1rem",
  textAlign: "center",
  lineHeight: "1.1rem",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.2rem",
    lineHeight: "1.3rem",
  },
});

// was: useStyles().buildIcon
const buildIconSx = (theme: Theme) => ({
  fontSize: "5rem",
  color: "#fff",
  marginBottom: "12px",
  [theme.breakpoints.up("sm")]: {
    fontSize: "8rem",
  },
});

const TrialsDisabled = () => {
  const { loggedIn } = useContext(AccountContext);

  return (
    <>
      {loggedIn ? <Navigate to="/videos" replace /> : null}
      {!loggedIn && <Header />}
      <Background>
        <Container maxWidth={"md"}>
          {/* v5+ Grid: `item` is gone and the breakpoint props collapse into
              `size`. No `spacing` on this container, so widths are unchanged. */}
          <Grid container sx={containerSx}>
            <Grid size={{ xs: 12 }}>
              <BuildIcon sx={buildIconSx} />
              <Typography variant="h3" sx={titleLargeSx}>
                We&apos;re working on it
              </Typography>
              <Typography variant="h3" sx={titleSx}>
                Free Trials are not available right now.
              </Typography>
              <Typography variant="body1" sx={bodyTextSx}>
                Check back soon.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Background>
      <Footer />
    </>
  );
};

export default TrialsDisabled;
